/**
 * Global error handlers for observability
 * These capture unhandled errors and promise rejections
 */

import { safeLog, getCurrentRoute, getCurrentUserId } from './log';

/**
 * Initialize global error handlers
 * Call this once in your app initialization
 */
export function initializeGlobalErrorHandlers(): void {
  // Handle unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    void safeLog({
      level: 'error',
      source: 'web',
      code: 'window_error',
      message: event?.error?.message || String(event?.message || 'window_error'),
      route: getCurrentRoute(),
      user_id: getCurrentUserId(),
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack?.slice(0, 500)
      }
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    void safeLog({
      level: 'error',
      source: 'web',
      code: 'unhandled_rejection',
      message: event?.reason?.message || String(event?.reason || 'unhandled_rejection'),
      route: getCurrentRoute(),
      user_id: getCurrentUserId(),
      context: {
        reason: String(event.reason),
        stack: event.reason?.stack?.slice(0, 500)
      }
    });
  });

  // Handle fetch errors globally
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);
      
      // Log failed requests
      if (!response.ok && response.status >= 400) {
        void safeLog({
          level: 'warn',
          source: 'web',
          code: 'fetch_error',
          message: `HTTP ${response.status}: ${response.statusText}`,
          route: getCurrentRoute(),
          user_id: getCurrentUserId(),
          context: {
            url: args[0]?.toString(),
            status: response.status,
            statusText: response.statusText
          }
        });
      }
      
      return response;
    } catch (error) {
      void safeLog({
        level: 'error',
        source: 'web',
        code: 'fetch_network_error',
        message: error instanceof Error ? error.message : 'Network error',
        route: getCurrentRoute(),
        user_id: getCurrentUserId(),
        context: {
          url: args[0]?.toString(),
          error: String(error)
        }
      });
      throw error;
    }
  };
}

/**
 * Cleanup global error handlers
 * Call this when unmounting the app
 */
export function cleanupGlobalErrorHandlers(): void {
  // Note: We don't actually remove the listeners as they're global
  // This is just for API consistency
  console.log('Global error handlers cleanup (no-op)');
}
